import React, { useState, useEffect } from 'react';
import { Building2, Plus, Pencil, Trash2, Search, X, Check, Loader2, ChevronDown, ChevronRight, Upload, FileText, Users, ExternalLink, Key, Scale, UserPlus } from 'lucide-react';
import { useAuth } from '../contexts/TestAuthContext';
import { useNavigate } from 'react-router-dom';
import { api } from '../utils/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

interface LawFirmClient {
  id: string;
  claimant_name: string;
  business_name: string;
  claimant_email: string;
  claimant_phone: string;
  event_type?: string;
  event_date?: string;
  status: string;
  created_at: string;
  claim_amount?: number;
  source?: string;
}

interface LawFirm {
  id: string;
  name: string;
  contact_name?: string;
  contact_email?: string;
  contact_phone?: string;
  address?: string;
  cases_count: number;
  created_at: string;
  clients?: LawFirmClient[];
  clients_total?: number;
  attorney_password_set?: boolean;
  clerk_org_id?: string;
  /** Side framing — absent/"plaintiff" is the default; "carrier" flips the brief
   *  headline + Exhibit C emphasis. Engine math never changes by side. */
  client_side?: 'plaintiff' | 'carrier';
}

export default function LawFirmManagement() {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const [lawFirms, setLawFirms] = useState<LawFirm[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showBulkImportModal, setShowBulkImportModal] = useState(false);
  const [editingFirm, setEditingFirm] = useState<LawFirm | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [expandedFirm, setExpandedFirm] = useState<string | null>(null);
  const [loadingCases, setLoadingCases] = useState<string | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    address: ''
  });

  // Bulk import state
  const [bulkImportFirm, setBulkImportFirm] = useState<LawFirm | null>(null);
  const [bulkImportData, setBulkImportData] = useState('');
  const [bulkImporting, setBulkImporting] = useState(false);

  // Attorney password state
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordFirm, setPasswordFirm] = useState<LawFirm | null>(null);
  const [attorneyPassword, setAttorneyPassword] = useState('');
  const [settingPassword, setSettingPassword] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Clerk organization state (PR-2, 2026-07-12 — staff-invite-only attorney
  // onboarding via Clerk Organizations, one org per firm)
  const [showClerkOrgModal, setShowClerkOrgModal] = useState(false);
  const [clerkOrgFirm, setClerkOrgFirm] = useState<LawFirm | null>(null);
  const [connectingClerkOrg, setConnectingClerkOrg] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [invitingAttorney, setInvitingAttorney] = useState(false);

  // Client-side (plaintiff | carrier) — platform-staff flip of a firm's bundle framing.
  const [clientSideFirm, setClientSideFirm] = useState<LawFirm | null>(null);
  const [pendingSide, setPendingSide] = useState<'plaintiff' | 'carrier'>('plaintiff');
  const [settingClientSide, setSettingClientSide] = useState(false);

  // Fetch law firms
  useEffect(() => {
    fetchLawFirms();
  }, []);

  const fetchLawFirms = async () => {
    try {
      setLoading(true);
      setFetchError(null);
      const token = await getToken();
      const data = await api.get('/api/v1/law-firms', {
        headers: { Authorization: `Bearer ${token}` },
        showErrorToast: false
      });
      setLawFirms(data.law_firms || []);
    } catch (error: any) {
      console.error('Error fetching law firms:', error);
      // NO mock fallback. On a forensic tool, showing fabricated "Anderson &
      // Associates / Baker Legal Group" firms as if they were real is worse than
      // showing nothing — surface the error and an empty list, never fake data.
      setLawFirms([]);
      setFetchError(
        error?.message || 'Could not load law firms. Check your connection and try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchClientsForFirm = async (firmId: string, firmName: string) => {
    setLoadingCases(firmName);
    try {
      const token = await getToken();
      const data = await api.get(`/api/v1/law-firms/${firmId}/clients?limit=50`, {
        headers: { Authorization: `Bearer ${token}` },
        showErrorToast: false
      });
      // Update the firm with its clients
      setLawFirms(prev => prev.map(f => 
        f.id === firmId 
          ? { ...f, clients: data.clients || [], clients_total: data.total || 0 }
          : f
      ));
    } catch (error) {
      console.error('Error fetching clients:', error);
    } finally {
      setLoadingCases(null);
    }
  };

  const handleExpandFirm = async (firm: LawFirm) => {
    if (expandedFirm === firm.id) {
      setExpandedFirm(null);
    } else {
      setExpandedFirm(firm.id);
      if (!firm.clients) {
        await fetchClientsForFirm(firm.id, firm.name);
      }
    }
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      setValidationError('Law firm name is required');
      return;
    }

    setSaving(true);
    setValidationError(null);
    try {
      const token = await getToken();
      const headers = { Authorization: `Bearer ${token}` };

      if (editingFirm) {
        await api.put(`/api/v1/law-firms/${editingFirm.id}`, formData, { headers });
      } else {
        await api.post('/api/v1/law-firms', formData, { headers });
      }

      await fetchLawFirms();
      handleCloseModal();
    } catch (error: any) {
      console.error('Error saving law firm:', error);
      // Do NOT add the firm to local state and do NOT close the modal on failure.
      // The prior behavior fabricated a "saved" firm the server never persisted —
      // on a forensic tool that silent lie is disqualifying. Keep the modal open
      // and surface the real error so the user knows it was NOT saved.
      setValidationError(
        error?.message || 'Could not save the law firm — it was not saved. Please try again.'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const token = await getToken();
      await api.delete(`/api/v1/law-firms/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Only drop it from the list once the server confirms the delete. The prior
      // behavior removed it unconditionally, so a failed delete still showed the
      // firm as gone (same fake-persistence lie as save) — never do that here.
      setLawFirms(prev => prev.filter(f => f.id !== id));
      setDeleteConfirm(null);
    } catch (error: any) {
      console.error('Error deleting:', error);
      setFetchError(
        error?.message || 'Could not delete the law firm — it was not deleted. Please try again.'
      );
      setDeleteConfirm(null);
    }
  };

  const handleBulkImport = async () => {
    if (!bulkImportFirm || !bulkImportData.trim()) return;
    
    setBulkImporting(true);
    try {
      // Parse CSV data: claimant_name, claimant_email, claimant_phone, business_name, business_address, event, event_date
      const lines = bulkImportData.trim().split('\n');
      const clients = lines.map(line => {
        const parts = line.split(',').map(p => p.trim());
        return {
          claimant_name: parts[0] || '',
          claimant_email: parts[1] || '',
          claimant_phone: parts[2] || '',
          business_name: parts[3] || '',
          business_address: parts[4] || '',
          event: parts[5] || '',
          event_date: parts[6] || ''
        };
      }).filter(c => c.claimant_name); // Filter out empty rows

      const token = await getToken();
      const result = await api.post(
        `/api/v1/law-firms/${bulkImportFirm.id}/bulk-import`,
        { clients },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSuccessMessage(`Successfully imported ${result.imported} clients for ${bulkImportFirm.name}!${result.errors?.length > 0 ? ` ${result.errors.length} errors occurred.` : ''}`);
      await fetchLawFirms();
      // Refresh the expanded firm's clients if it was the one we imported to
      if (expandedFirm === bulkImportFirm.id) {
        await fetchClientsForFirm(bulkImportFirm.id, bulkImportFirm.name);
      }
      
      setShowBulkImportModal(false);
      setBulkImportData('');
      setBulkImportFirm(null);
    } catch (error: any) {
      console.error('Error bulk importing:', error);
      setValidationError(error.message || 'Error importing clients. Please try again.');
    } finally {
      setBulkImporting(false);
    }
  };

  // Set attorney password for law firm
  const handleSetAttorneyPassword = async () => {
    if (!passwordFirm || !attorneyPassword) return;
    
    try {
      setSettingPassword(true);
      const token = await getToken();
      await api.post(
        `/api/v1/attorney/admin/law-firms/${passwordFirm.id}/set-password`,
        { password: attorneyPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSuccessMessage(`Attorney portal access set for ${passwordFirm.name}! Login: ${passwordFirm.contact_email}`);
      setShowPasswordModal(false);
      setPasswordFirm(null);
      setAttorneyPassword('');
      await fetchLawFirms();
    } catch (error: any) {
      console.error('Error setting attorney password:', error);
      setValidationError(error.message || 'Error setting password. Please try again.');
    } finally {
      setSettingPassword(false);
    }
  };

  const openPasswordModal = (firm: LawFirm) => {
    setPasswordFirm(firm);
    setAttorneyPassword('');
    setShowPasswordModal(true);
  };

  // Connect a law firm to a Clerk Organization (idempotent — no-ops server
  // side if already connected).
  const handleConnectClerkOrg = async () => {
    if (!clerkOrgFirm) return;
    try {
      setConnectingClerkOrg(true);
      const token = await getToken();
      const result = await api.post(
        `/api/v1/law-firms/${clerkOrgFirm.id}/clerk-org`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setClerkOrgFirm({ ...clerkOrgFirm, clerk_org_id: result.clerk_org_id });
      setLawFirms(prev => prev.map(f =>
        f.id === clerkOrgFirm.id ? { ...f, clerk_org_id: result.clerk_org_id } : f
      ));
    } catch (error: any) {
      console.error('Error connecting Clerk organization:', error);
      setValidationError(error.message || 'Error connecting to Clerk. Please try again.');
    } finally {
      setConnectingClerkOrg(false);
    }
  };

  // Invite an attorney into the firm's Clerk org by email (staff-invite
  // only — no self-serve attorney signup).
  const handleInviteAttorney = async () => {
    if (!clerkOrgFirm || !inviteEmail) return;
    try {
      setInvitingAttorney(true);
      const token = await getToken();
      await api.post(
        `/api/v1/law-firms/${clerkOrgFirm.id}/attorneys/invite`,
        { email: inviteEmail },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccessMessage(`Invited ${inviteEmail} to ${clerkOrgFirm.name}'s attorney portal.`);
      setInviteEmail('');
    } catch (error: any) {
      console.error('Error inviting attorney:', error);
      setValidationError(error.message || 'Error inviting attorney. Please try again.');
    } finally {
      setInvitingAttorney(false);
    }
  };

  const openClerkOrgModal = (firm: LawFirm) => {
    setClerkOrgFirm(firm);
    setInviteEmail('');
    setShowClerkOrgModal(true);
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setEditingFirm(null);
    setFormData({
      name: '',
      contact_name: '',
      contact_email: '',
      contact_phone: '',
      address: ''
    });
  };

  const handleEdit = (firm: LawFirm) => {
    setEditingFirm(firm);
    setFormData({
      name: firm.name,
      contact_name: firm.contact_name || '',
      contact_email: firm.contact_email || '',
      contact_phone: firm.contact_phone || '',
      address: firm.address || ''
    });
    setShowAddModal(true);
  };

  const openBulkImport = (firm: LawFirm) => {
    setBulkImportFirm(firm);
    setShowBulkImportModal(true);
  };

  // Open the side-flip confirm modal, defaulting the radio to the firm's current side.
  const openClientSideModal = (firm: LawFirm) => {
    setClientSideFirm(firm);
    setPendingSide(firm.client_side === 'carrier' ? 'carrier' : 'plaintiff');
    setValidationError(null);
  };

  // Platform-staff flip of a firm's side. Requires acknowledge=true (the modal IS the
  // confirm). Engine math never changes by side — only brief/bundle framing.
  const handleSetClientSide = async () => {
    if (!clientSideFirm) return;
    setSettingClientSide(true);
    setValidationError(null);
    try {
      const token = await getToken();
      const headers = { Authorization: `Bearer ${token}` };
      const res = await api.put<{ client_side: 'plaintiff' | 'carrier' }>(
        `/api/v1/law-firms/${clientSideFirm.id}/client-side`,
        { client_side: pendingSide, acknowledge: true },
        { headers },
      );
      const newSide = res?.client_side || pendingSide;
      setLawFirms((prev) => prev.map((f) => (f.id === clientSideFirm.id ? { ...f, client_side: newSide } : f)));
      setSuccessMessage(
        `${clientSideFirm.name} is now framed as ${newSide.toUpperCase()}. Engine math unchanged.`,
      );
      setClientSideFirm(null);
    } catch (error: any) {
      setValidationError(error?.message || 'Could not update the firm side — it was not changed.');
    } finally {
      setSettingClientSide(false);
    }
  };

  const filteredFirms = lawFirms.filter(firm =>
    firm.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    firm.contact_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getStatusBadge = (status: string) => {
    const configs: Record<string, React.CSSProperties> = {
      completed: { backgroundColor: 'transparent', color: 'var(--color-verified)' },
      draft: { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-brand)' },
      in_progress: { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' },
    };
    const config = configs[status] || { backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' };
    return (
      <span className="px-2 py-0.5 rounded-full text-xs font-medium" style={config}>
        {status.replace('_', ' ')}
      </span>
    );
  };

  return (
    <div>
      {/* Header */}
      <div className="px-6 py-4 rounded-t-xl" style={{ backgroundColor: 'var(--color-brand)' }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Building2 className="w-6 h-6" style={{ color: 'var(--color-on-brand)' }} />
            <div>
              <h2 className="text-lg font-bold" style={{ color: 'var(--color-on-brand)' }}>Law Firm Management</h2>
              <p className="text-sm" style={{ color: 'var(--color-on-brand)' }}>Manage your referring law firms and their clients</p>
            </div>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-sm transition-colors"
            style={{ backgroundColor: 'var(--color-bg-surface)', color: 'var(--color-needs-human-text)' }}
          >
            <Plus className="w-4 h-4" />
            Add Law Firm
          </button>
        </div>
      </div>

      <div className="p-6 rounded-b-xl border border-t-0" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--color-text-dim)' }} />
          <input
            type="text"
            placeholder="Search law firms..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border rounded-lg focus:ring-2 focus:border-transparent"
            style={{ borderColor: 'var(--color-border)' }}
          />
        </div>

        {/* Law Firms List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--color-needs-human-text)' }} />
            <span className="ml-2" style={{ color: 'var(--color-text-secondary)' }}>Loading law firms...</span>
          </div>
        ) : fetchError ? (
          <div className="text-center py-12">
            <Building2 className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--color-text-ghost)' }} />
            <p className="font-medium" style={{ color: 'var(--color-blocker)' }}>Couldn't load law firms</p>
            <p className="text-sm mt-1" style={{ color: 'var(--color-text-muted)' }}>{fetchError}</p>
            <button
              onClick={fetchLawFirms}
              className="mt-4 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              style={{ backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-primary)' }}
            >
              Retry
            </button>
          </div>
        ) : filteredFirms.length === 0 ? (
          <div className="text-center py-12">
            <Building2 className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--color-text-ghost)' }} />
            <p style={{ color: 'var(--color-text-muted)' }}>No law firms found</p>
            <button
              onClick={() => setShowAddModal(true)}
              className="mt-4 px-4 py-2 rounded-lg text-sm transition-colors"
              style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
            >
              Add Your First Law Firm
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredFirms.map((firm) => (
              <div key={firm.id} className="border rounded-xl overflow-hidden" style={{ borderColor: 'var(--color-border)' }}>
                {/* Firm Row */}
                <div
                  className={`flex items-center justify-between p-4 cursor-pointer transition-colors ${expandedFirm === firm.id ? 'border-b' : ''}`}
                  style={{ backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)' }}
                  onClick={() => handleExpandFirm(firm)}
                >
                  <div className="flex items-center gap-4">
                    <button className="p-1 rounded">
                      {expandedFirm === firm.id ? (
                        <ChevronDown className="w-5 h-5" style={{ color: 'var(--color-text-muted)' }} />
                      ) : (
                        <ChevronRight className="w-5 h-5" style={{ color: 'var(--color-text-muted)' }} />
                      )}
                    </button>
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'var(--color-needs-human-tint)' }}>
                      <Building2 className="w-6 h-6" style={{ color: 'var(--color-needs-human-text)' }} />
                    </div>
                    <div>
                      <h3 className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>{firm.name}</h3>
                      <div className="flex items-center gap-4 mt-1 text-sm" style={{ color: 'var(--color-text-muted)' }}>
                        {firm.contact_name && <span>{firm.contact_name}</span>}
                        {firm.contact_email && (
                          <span style={{ color: 'var(--color-needs-human-text)' }}>{firm.contact_email}</span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="text-right mr-4">
                      <p className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>{firm.cases_count}</p>
                      <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>cases</p>
                    </div>
                    
                    <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => openBulkImport(firm)}
                        className="p-2 rounded-lg transition-colors"
                        style={{ color: 'var(--color-text-dim)' }}
                        title="Bulk Import Clients"
                      >
                        <Upload className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => openPasswordModal(firm)}
                        className="p-2 rounded-lg transition-colors"
                        style={{ color: firm.attorney_password_set ? 'var(--color-verified)' : 'var(--color-text-dim)' }}
                        title={firm.attorney_password_set ? "Update Attorney Portal Access" : "Set Attorney Portal Access"}
                      >
                        <Key className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => openClerkOrgModal(firm)}
                        className="p-2 rounded-lg transition-colors"
                        style={{ color: firm.clerk_org_id ? 'var(--color-verified)' : 'var(--color-text-dim)' }}
                        title={firm.clerk_org_id ? "Invite Attorneys (Clerk Org connected)" : "Connect Clerk Organization"}
                        data-testid="clerk-org-button"
                      >
                        <UserPlus className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => openClientSideModal(firm)}
                        className="p-2 rounded-lg transition-colors"
                        style={{ color: firm.client_side === 'carrier' ? 'var(--color-needs-human-text)' : 'var(--color-text-dim)' }}
                        title={firm.client_side === 'carrier' ? 'Side: Carrier — change framing' : 'Side: Plaintiff — change framing'}
                        data-testid="client-side-button"
                      >
                        <Scale className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleEdit(firm)}
                        className="p-2 rounded-lg transition-colors"
                        style={{ color: 'var(--color-text-dim)' }}
                        title="Edit"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>

                      {deleteConfirm === firm.id ? (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleDelete(firm.id)}
                            className="p-2 rounded-lg transition-colors"
                            style={{ color: 'var(--color-on-brand)', backgroundColor: 'var(--color-brand)' }}
                            title="Confirm delete"
                          >
                            <Check className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setDeleteConfirm(null)}
                            className="p-2 rounded-lg transition-colors"
                            style={{ color: 'var(--color-text-dim)' }}
                            title="Cancel"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setDeleteConfirm(firm.id)}
                          className="p-2 rounded-lg transition-colors"
                          style={{ color: 'var(--color-text-dim)' }}
                          title="Delete"
                          disabled={firm.cases_count > 0}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Expanded Clients */}
                {expandedFirm === firm.id && (
                  <div className="p-4" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
                    {loadingCases === firm.name ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-5 h-5 animate-spin" style={{ color: 'var(--color-needs-human-text)' }} />
                        <span className="ml-2 text-sm" style={{ color: 'var(--color-text-muted)' }}>Loading clients...</span>
                      </div>
                    ) : firm.clients && firm.clients.length > 0 ? (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="text-sm font-semibold flex items-center gap-2" style={{ color: 'var(--color-text-secondary)' }}>
                            <Users className="w-4 h-4" />
                            Clients ({firm.clients_total || firm.clients.length})
                          </h4>
                          <div className="flex items-center gap-3">
                            <button
                              onClick={(e) => { e.stopPropagation(); openBulkImport(firm); }}
                              className="text-xs flex items-center gap-1"
                              style={{ color: 'var(--color-verified)' }}
                            >
                              <Upload className="w-3 h-3" /> Import More
                            </button>
                            <button
                              onClick={() => navigate(`/dashboard?law_firm=${encodeURIComponent(firm.name)}`)}
                              className="text-xs flex items-center gap-1"
                              style={{ color: 'var(--color-needs-human-text)' }}
                            >
                              View All <ExternalLink className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-left text-xs uppercase border-b" style={{ color: 'var(--color-text-label)', borderColor: 'var(--color-border)' }}>
                              <th className="pb-2 font-medium">Claimant</th>
                              <th className="pb-2 font-medium">Business</th>
                              <th className="pb-2 font-medium">Contact</th>
                              <th className="pb-2 font-medium">Event</th>
                              <th className="pb-2 font-medium">Status</th>
                              <th className="pb-2 font-medium">Date</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-[color:var(--color-border-faint)]">
                            {firm.clients.slice(0, 15).map((c) => (
                              <tr
                                key={c.id}
                                className="cursor-pointer"
                                onClick={() => c.status === 'draft'
                                  ? navigate(`/wizard-v2?claim=${c.id}`)
                                  : navigate(`/report/${c.id}`)
                                }
                              >
                                <td className="py-2">
                                  <div className="font-medium" style={{ color: 'var(--color-text-primary)' }}>{c.claimant_name || '-'}</div>
                                  {c.claimant_email && (
                                    <div className="text-xs" style={{ color: 'var(--color-text-muted)' }}>{c.claimant_email}</div>
                                  )}
                                </td>
                                <td className="py-2" style={{ color: 'var(--color-text-secondary)' }}>{c.business_name || '-'}</td>
                                <td className="py-2" style={{ color: 'var(--color-text-secondary)' }}>{c.claimant_phone || '-'}</td>
                                <td className="py-2">
                                  <div style={{ color: 'var(--color-text-primary)' }}>{c.event_type || '-'}</div>
                                  {c.event_date && (
                                    <div className="text-xs" style={{ color: 'var(--color-text-muted)' }}>{c.event_date}</div>
                                  )}
                                </td>
                                <td className="py-2">
                                  {getStatusBadge(c.status)}
                                  {c.source === 'bulk_import' && (
                                    <span className="ml-1 text-[10px]" style={{ color: 'var(--color-text-dim)' }}>(imported)</span>
                                  )}
                                </td>
                                <td className="py-2" style={{ color: 'var(--color-text-muted)' }}>
                                  {c.created_at ? new Date(c.created_at).toLocaleDateString() : '-'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {(firm.clients_total || firm.clients.length) > 15 && (
                          <p className="text-xs text-center pt-2" style={{ color: 'var(--color-text-muted)' }}>
                            Showing 15 of {firm.clients_total || firm.clients.length} clients
                          </p>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <FileText className="w-10 h-10 mx-auto mb-2" style={{ color: 'var(--color-text-ghost)' }} />
                        <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>No clients yet</p>
                        <button
                          onClick={() => openBulkImport(firm)}
                          className="mt-2 text-sm"
                          style={{ color: 'var(--color-needs-human-text)' }}
                        >
                          Import clients
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Stats Footer */}
        <div className="mt-6 pt-6 border-t" style={{ borderColor: 'var(--color-border)' }}>
          <div className="flex items-center justify-between text-sm" style={{ color: 'var(--color-text-muted)' }}>
            <span>{lawFirms.length} law firm{lawFirms.length !== 1 ? 's' : ''} total</span>
            <span>{lawFirms.reduce((sum, f) => sum + f.cases_count, 0)} total cases</span>
          </div>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="rounded-2xl shadow-2xl max-w-lg w-full mx-4 overflow-hidden" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
            <div className="px-6 py-4" style={{ backgroundColor: 'var(--color-brand)' }}>
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold" style={{ color: 'var(--color-on-brand)' }}>
                  {editingFirm ? 'Edit Law Firm' : 'Add New Law Firm'}
                </h3>
                <button onClick={handleCloseModal} style={{ color: 'var(--color-on-brand)' }}>
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              {validationError && (
                <p className="text-sm" style={{ color: 'var(--color-needs-human-text)' }}>{validationError}</p>
              )}
              {successMessage && (
                <p className="text-sm" style={{ color: 'var(--color-verified)' }}>{successMessage}</p>
              )}
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                  Law Firm Name <span style={{ color: 'var(--color-needs-human-text)' }}>*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => { setFormData({ ...formData, name: e.target.value }); setValidationError(null); }}
                  placeholder="e.g., Smith & Associates LLP"
                  className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:border-transparent"
                  style={{ borderColor: 'var(--color-border)' }}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>Contact Name</label>
                  <input
                    type="text"
                    value={formData.contact_name}
                    onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                    placeholder="Primary contact"
                    className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:border-transparent"
                    style={{ borderColor: 'var(--color-border)' }}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>Contact Email</label>
                  <input
                    type="email"
                    value={formData.contact_email}
                    onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                    placeholder="email@lawfirm.com"
                    className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:border-transparent"
                    style={{ borderColor: 'var(--color-border)' }}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>Phone Number</label>
                  <input
                    type="tel"
                    value={formData.contact_phone}
                    onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                    placeholder="(555) 123-4567"
                    className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:border-transparent"
                    style={{ borderColor: 'var(--color-border)' }}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>Address</label>
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="City, State"
                    className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:border-transparent"
                    style={{ borderColor: 'var(--color-border)' }}
                  />
                </div>
              </div>
            </div>

            <div className="px-6 py-4 flex justify-end gap-3" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
              <button
                onClick={handleCloseModal}
                className="px-4 py-2 rounded-lg font-medium transition-colors"
                style={{ color: 'var(--color-text-secondary)', backgroundColor: 'var(--color-bg-hover)' }}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !formData.name.trim()}
                className="px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                {editingFirm ? 'Save Changes' : 'Add Law Firm'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Import Modal */}
      {showBulkImportModal && bulkImportFirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="rounded-2xl shadow-2xl max-w-2xl w-full mx-4 overflow-hidden" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
            <div className="px-6 py-4" style={{ backgroundColor: 'var(--color-verified)' }}>
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold" style={{ color: 'var(--color-on-brand)' }}>Bulk Import Clients</h3>
                  <p className="text-sm" style={{ color: 'var(--color-on-brand)' }}>Import multiple clients for {bulkImportFirm.name}</p>
                </div>
                <button
                  onClick={() => { setShowBulkImportModal(false); setBulkImportFirm(null); setBulkImportData(''); }}
                  style={{ color: 'var(--color-on-brand)' }}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="p-6">
              <div className="mb-4 p-4 border rounded-lg" style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }}>
                <p className="text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>CSV Format (one client per line):</p>
                <code className="text-xs block p-2 rounded font-mono" style={{ color: 'var(--color-needs-human-text)', backgroundColor: 'var(--color-needs-human-tint)' }}>
                  claimant_name, email, phone, business_name, address, event, event_date
                </code>
                <p className="text-xs mt-2" style={{ color: 'var(--color-needs-human-text)' }}>
                  Example: John Smith, john@email.com, 555-1234, Pacific Surf Shop, 123 Beach Blvd, Fire, 2024-06-15
                </p>
                <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>
                  <strong>Required:</strong> claimant_name (first column). All other fields are optional.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>
                  Paste CSV Data ({bulkImportData.split('\n').filter(l => l.trim()).length} clients detected)
                </label>
                <textarea
                  value={bulkImportData}
                  onChange={(e) => setBulkImportData(e.target.value)}
                  placeholder="John Smith, john@email.com, 555-1234, Pacific Surf Shop, 123 Beach Blvd, Fire, 2024-06-15&#10;Jane Doe, jane@email.com, 555-5678, Joe's Diner, 456 Main St, Hurricane, 2024-07-20&#10;Bob Wilson, bob@email.com, , Corner Store, 789 Oak Ave, ,&#10;..."
                  rows={12}
                  className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent font-mono text-sm"
                  style={{ borderColor: 'var(--color-border)' }}
                  data-testid="bulk-import-textarea"
                />
              </div>

              <div className="mt-3 flex items-start gap-2 text-xs" style={{ color: 'var(--color-text-muted)' }}>
                <span className="inline-block w-4 h-4 rounded-full flex-shrink-0 flex items-center justify-center mt-0.5" style={{ backgroundColor: 'transparent', color: 'var(--color-verified)' }}>✓</span>
                <span>Supports up to 1,000 clients per import. Copy/paste directly from Excel or Google Sheets.</span>
              </div>
            </div>

            <div className="px-6 py-4 flex justify-between items-center" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
              <span className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
                {bulkImportData.split('\n').filter(l => l.trim()).length} clients ready to import
              </span>
              <div className="flex gap-3">
                <button
                  onClick={() => { setShowBulkImportModal(false); setBulkImportFirm(null); setBulkImportData(''); }}
                  className="px-4 py-2 rounded-lg font-medium transition-colors"
                  style={{ color: 'var(--color-text-secondary)', backgroundColor: 'var(--color-bg-hover)' }}
                  data-testid="bulk-import-cancel"
                >
                  Cancel
                </button>
                <button
                  onClick={handleBulkImport}
                  disabled={bulkImporting || !bulkImportData.trim()}
                  className="px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  style={{ backgroundColor: 'var(--color-verified)', color: 'var(--color-on-brand)' }}
                  data-testid="bulk-import-submit"
                >
                  {bulkImporting && <Loader2 className="w-4 h-4 animate-spin" />}
                  Import Clients
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Attorney Password Modal */}
      {showPasswordModal && passwordFirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
            <div className="px-6 py-4" style={{ backgroundColor: 'var(--color-brand)' }}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Scale className="w-5 h-5" style={{ color: 'var(--color-on-brand)' }} />
                  <h3 className="text-lg font-bold" style={{ color: 'var(--color-on-brand)' }}>
                    {passwordFirm.attorney_password_set ? 'Update' : 'Set'} Attorney Portal Access
                  </h3>
                </div>
                <button onClick={() => { setShowPasswordModal(false); setPasswordFirm(null); setAttorneyPassword(''); }} style={{ color: 'var(--color-on-brand)' }}>
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div className="rounded-lg p-4" style={{ backgroundColor: 'var(--color-needs-human-tint)' }}>
                <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                  <strong>{passwordFirm.name}</strong> will be able to access their released cases at:
                </p>
                <p className="text-sm font-mono mt-1" style={{ color: 'var(--color-needs-human-text)' }}>
                  {window.location.origin}/attorney/login
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                  Login Email
                </label>
                <input
                  type="text"
                  value={passwordFirm.contact_email || 'No email set'}
                  disabled
                  className="w-full px-4 py-2.5 border rounded-lg"
                  style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-muted)' }}
                />
                {!passwordFirm.contact_email && (
                  <p className="text-xs mt-1" style={{ color: 'var(--color-needs-human-text)' }}>Please add a contact email to this law firm first.</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                  Password <span style={{ color: 'var(--color-needs-human-text)' }}>*</span>
                </label>
                <input
                  type="password"
                  value={attorneyPassword}
                  onChange={(e) => setAttorneyPassword(e.target.value)}
                  placeholder="Set a secure password"
                  className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:border-transparent"
                  style={{ borderColor: 'var(--color-border)' }}
                  data-testid="attorney-password-input"
                />
                <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>
                  This password will be used by the attorney to access their portal.
                </p>
              </div>
            </div>

            <div className="px-6 py-4 flex justify-end gap-3" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
              <button
                onClick={() => { setShowPasswordModal(false); setPasswordFirm(null); setAttorneyPassword(''); }}
                className="px-4 py-2 rounded-lg font-medium transition-colors"
                style={{ color: 'var(--color-text-secondary)', backgroundColor: 'var(--color-bg-hover)' }}
              >
                Cancel
              </button>
              <button
                onClick={handleSetAttorneyPassword}
                disabled={settingPassword || !attorneyPassword || !passwordFirm.contact_email}
                className="px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
                data-testid="set-attorney-password-button"
              >
                {settingPassword && <Loader2 className="w-4 h-4 animate-spin" />}
                {passwordFirm.attorney_password_set ? 'Update Password' : 'Enable Portal Access'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Clerk Organization Modal (PR-2, 2026-07-12) */}
      {showClerkOrgModal && clerkOrgFirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
            <div className="px-6 py-4" style={{ backgroundColor: 'var(--color-brand)' }}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <UserPlus className="w-5 h-5" style={{ color: 'var(--color-on-brand)' }} />
                  <h3 className="text-lg font-bold" style={{ color: 'var(--color-on-brand)' }}>
                    Attorney Access ({clerkOrgFirm.name})
                  </h3>
                </div>
                <button onClick={() => { setShowClerkOrgModal(false); setClerkOrgFirm(null); setInviteEmail(''); }} style={{ color: 'var(--color-on-brand)' }}>
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div className="rounded-lg p-4" style={{ backgroundColor: 'var(--color-needs-human-tint)' }}>
                <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                  Attorneys sign in with Clerk, not a shared portal password. Connect this
                  firm to a Clerk organization once, then invite attorneys by email —
                  staff-invite only, no self-serve signup.
                </p>
              </div>

              {!clerkOrgFirm.clerk_org_id ? (
                <button
                  onClick={handleConnectClerkOrg}
                  disabled={connectingClerkOrg}
                  className="w-full px-4 py-2.5 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
                  data-testid="connect-clerk-org-button"
                >
                  {connectingClerkOrg && <Loader2 className="w-4 h-4 animate-spin" />}
                  Connect Clerk Organization
                </button>
              ) : (
                <>
                  <p className="text-xs" style={{ color: 'var(--color-verified)' }}>
                    Connected to Clerk organization {clerkOrgFirm.clerk_org_id}
                  </p>
                  <div>
                    <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                      Attorney Email
                    </label>
                    <input
                      type="email"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      placeholder="attorney@firm.com"
                      className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:border-transparent"
                      style={{ borderColor: 'var(--color-border)' }}
                      data-testid="invite-attorney-email-input"
                    />
                  </div>
                  <button
                    onClick={handleInviteAttorney}
                    disabled={invitingAttorney || !inviteEmail}
                    className="w-full px-4 py-2.5 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
                    data-testid="invite-attorney-button"
                  >
                    {invitingAttorney && <Loader2 className="w-4 h-4 animate-spin" />}
                    Invite Attorney
                  </button>
                </>
              )}
            </div>

            <div className="px-6 py-4 flex justify-end" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
              <button
                onClick={() => { setShowClerkOrgModal(false); setClerkOrgFirm(null); setInviteEmail(''); }}
                className="px-4 py-2 rounded-lg font-medium transition-colors"
                style={{ color: 'var(--color-text-secondary)', backgroundColor: 'var(--color-bg-hover)' }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Firm side (plaintiff | carrier) — platform-staff flip with hard confirm */}
      {clientSideFirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div
            className="w-full max-w-md rounded-xl border p-6"
            style={{ backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)' }}
          >
            <div className="flex items-center gap-3 mb-1">
              <Scale className="w-5 h-5" style={{ color: 'var(--color-needs-human-text)' }} />
              <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                Firm side
              </h3>
            </div>
            <p className="text-sm mb-4" style={{ color: 'var(--color-text-muted)' }}>
              {clientSideFirm.name}
            </p>

            <div
              className="rounded-lg border p-3 mb-4 text-sm"
              style={{ borderColor: 'var(--color-brand)', color: 'var(--color-text-secondary)' }}
            >
              This reframes the firm&apos;s War Room brief headline and court-bundle emphasis to the
              selected side. <strong>Engine math is identical both sides — only framing changes.</strong>{' '}
              A wrong setting on a plaintiff firm silently mis-frames their bundle.
            </div>

            <div className="flex gap-2 mb-4">
              {(['plaintiff', 'carrier'] as const).map((side) => {
                const active = pendingSide === side;
                return (
                  <button
                    key={side}
                    onClick={() => setPendingSide(side)}
                    className="flex-1 rounded-lg border px-4 py-2 text-sm font-semibold capitalize transition-colors"
                    style={{
                      borderColor: active ? 'var(--color-brand)' : 'var(--color-border)',
                      color: active ? 'var(--color-needs-human-text)' : 'var(--color-text-muted)',
                      backgroundColor: active ? 'var(--color-needs-human-tint)' : 'transparent',
                    }}
                  >
                    {side}
                  </button>
                );
              })}
            </div>

            {validationError && (
              <p className="text-sm mb-3" style={{ color: 'var(--color-needs-human-text)' }}>
                {validationError}
              </p>
            )}

            <div className="flex justify-end gap-2">
              <button
                onClick={() => setClientSideFirm(null)}
                className="px-4 py-2 rounded-lg text-sm font-semibold"
                style={{ color: 'var(--color-text-muted)' }}
                disabled={settingClientSide}
              >
                Cancel
              </button>
              <button
                onClick={handleSetClientSide}
                disabled={settingClientSide || pendingSide === (clientSideFirm.client_side || 'plaintiff')}
                data-testid="client-side-confirm"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50"
                style={{ color: 'var(--color-on-brand)', backgroundColor: 'var(--color-brand)' }}
              >
                {settingClientSide && <Loader2 className="w-4 h-4 animate-spin" />}
                Set to {pendingSide}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
